import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useInvoiceTemplates } from '~/api/invoiceTemplates';
import { QueryKeys } from '~/api/keys';
import { ErrorState, useTable } from '~/components';
import { ColumnIds } from '~/constants/table';
import { useDebouncedState } from '~/hooks/useDebouncedState';
import { r, routes } from '~/providers/RouterProvider/router.routes';

import { useInvoiceTemplatesTableColumns, usePrefetchNextPage } from './InvoiceTemplatesTable.hooks';
import {
  useInvoiceTemplatesTableColumnFilters,
  useInvoiceTemplatesTableGlobalFilter,
  useInvoiceTemplatesTablePaginationState,
  useInvoiceTemplatesTableSorting,
} from './InvoiceTemplatesTable.nuqs';

export const InvoiceTemplatesTable = () => {
  const [pagination, setPagination] = useInvoiceTemplatesTablePaginationState();
  const [sorting, setSorting] = useInvoiceTemplatesTableSorting();
  const [globalFilter, setGlobalFilter] = useInvoiceTemplatesTableGlobalFilter();
  const [columnFilters, setColumnFilters] = useInvoiceTemplatesTableColumnFilters();

  const [debouncedGlobalFilter] = useDebouncedState(globalFilter, 200);
  const queryMetadata = useMemo(
    () => ({
      pagination,
      sorting,
      filters: {
        search: debouncedGlobalFilter.trim(),
        clientId: columnFilters.find((filter) => filter.id === ColumnIds.invoiceTemplatesClient)?.value ?? null,
        brandId: columnFilters.find((filter) => filter.id === ColumnIds.invoiceTemplatesBrand)?.value ?? null,
        templateType: columnFilters.find((filter) => filter.id === ColumnIds.invoiceTemplatesType)?.value ?? null,
      },
    }),
    [debouncedGlobalFilter, columnFilters, pagination, sorting],
  );

  const { data, isError, isPending, isPlaceholderData } = useInvoiceTemplates(queryMetadata);
  const queryClient = useQueryClient();

  usePrefetchNextPage(data?.pagination.nextPage ?? null, queryMetadata);

  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'invoiceTemplates', 'filters']);

  const columns = useInvoiceTemplatesTableColumns();
  const [table, Table] = useTable({
    data: data?.data ?? [],
    columns,
    initialState: {
      columnVisibility: {
        [ColumnIds.invoiceTemplatesBrand]: false,
      },
    },
    state: { pagination, sorting, globalFilter, columnFilters },
    enableSortingRemoval: false,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onGlobalFilterChange: (...args) => {
      setGlobalFilter(...args);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    onColumnFiltersChange: (...args) => {
      setColumnFilters(...args);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    rowCount: data?.pagination.totalResults,
  });

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          queryClient.resetQueries({ queryKey: QueryKeys.invoiceTemplates.index.metadata(queryMetadata) });
          setPagination(null);
          setSorting(null);
          setGlobalFilter(null);
          setColumnFilters(null);
        }}
        title={t('invoiceTemplates:overview.error.title')}
      />
    );
  }

  return (
    <Table.Root table={table}>
      <Table.Menu>
        <Table.Search />

        <Table.Filters>
          <Table.Filter columnId={ColumnIds.invoiceTemplatesClient} type="combobox" />
          <Table.Filter columnId={ColumnIds.invoiceTemplatesBrand} hideWhen="singleOption" type="select" />
          <Table.Filter columnId={ColumnIds.invoiceTemplatesType} type="select" />
        </Table.Filters>
      </Table.Menu>

      <Table.Grid isLoading={isPending || isPlaceholderData}>
        <Table.Header />
        <Table.Body>
          {(row) => (
            <Table.Row
              actionLabel={t('invoiceTemplates:actions.details')}
              onClick={({ id }) => navigate(r(routes.showInvoiceTemplate, { invoiceTemplateId: id }))}
              row={row}
            />
          )}
        </Table.Body>
      </Table.Grid>

      <Table.Pagination showPageSizeOptions />
    </Table.Root>
  );
};
